// File: src/components/layout/StudentLayout.jsx
//
// Layout for the Cognito-student-only branch of App.js — deliberately
// separate from PageLayout/Header/Sidebar, which all hard-depend on
// useAuth0() for user info and logout. Kept intentionally small: four
// nav links, a phone-number identity display (students may have no name
// on file yet), and a sign-out action that clears the Cognito session
// rather than calling Auth0's logout.

import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { LogOut, Menu, X, LayoutDashboard, BookOpen, TrendingUp, Wallet, User } from 'lucide-react';
import { useStudentAuth } from '../../context/StudentAuthContext';
import '../../styles/unified.css';

const NAV_ITEMS = [
  { name: 'Dashboard',   icon: LayoutDashboard, path: '/dashboard' },
  { name: 'Book Slots',  icon: BookOpen,        path: '/bookings' },
  { name: 'My Progress', icon: TrendingUp,      path: '/my-progress' },
  { name: 'My Payments', icon: Wallet,          path: '/my-payments' },
  { name: 'My Account',  icon: User,            path: '/my-account' },
];

const StudentLayout = ({ children }) => {
  const { studentPhone, studentLogoutAndClear } = useStudentAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    studentLogoutAndClear();
    navigate('/student-login');
  };

  return (
    <div className="app-with-layout">
      <header className="app-header-new">
        {/* .student-header-inner overrides just maxWidth/padding on top of
            .header-container's flex layout (rather than editing that shared
            rule, which Header.jsx also uses for the instructor layout) so
            this row's left edge lines up exactly with .page-container's —
            same 1400px / 2rem used everywhere student content renders,
            including at the same mobile breakpoint. */}
        <div className="header-container student-header-inner">
          <button
            className="mobile-menu-toggle"
            onClick={() => setMobileOpen(o => !o)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          <div className="header-logo">
            <span className="logo-text">DriveZoe — Student</span>
          </div>

          <div className="header-actions">
            <div className="user-info" style={{ cursor: 'default' }}>
              <div className="user-avatar-placeholder">
                <User size={20} />
              </div>
              <span className="user-name">{studentPhone || 'Student'}</span>
            </div>

            <button onClick={handleLogout} className="btn-logout-header">
              <LogOut size={18} />
              <span className="logout-text">Sign out</span>
            </button>
          </div>
        </div>

        {/* Simple horizontal nav — four links don't warrant a full collapsible
            sidebar like the instructor side has. Outer <nav> stays full-bleed
            for the background strip; .student-nav-inner is what's actually
            constrained to line up with the header and page content above/below it. */}
        <nav
          className={`student-nav ${mobileOpen ? 'student-nav-open' : ''}`}
          style={{ borderTop: '1px solid #e5e7eb', background: '#fafafa' }}
        >
          <div className="student-nav-inner" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {NAV_ITEMS.map(item => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                style={({ isActive }) => ({
                  display: 'flex', alignItems: 'center', gap: '0.4rem',
                  padding: '0.45rem 0.85rem', borderRadius: '0.5rem',
                  fontSize: '0.88rem', fontWeight: 600, textDecoration: 'none',
                  color: isActive ? '#4f46e5' : '#4b5563',
                  background: isActive ? '#eef2ff' : 'transparent'
                })}
              >
                <item.icon size={16} />
                {item.name}
              </NavLink>
            ))}
          </div>
        </nav>
      </header>

      <main className="main-content-with-sidebar">
        {children}
      </main>
    </div>
  );
};

export default StudentLayout;
